import type { Device } from 'src/types/Device';
import { compareFWVersions } from 'src/helpers/firmware_compare_helpers';
import {
	get_gen_2_component_from_category,
	isGen2EM,
	isProThreeEM,
} from 'src/helpers/interface_helpers';

export const EMETER_CT_TYPE_MIN_VER = '1.4.0';
export const EMETER_RESET_COUNTERS_MIN_VER = '1.2.0';

export const EMETER_ALARM_VARIABLES = ['voltage', 'current', 'power'] as const;
export type EmeterAlarmVariable = (typeof EMETER_ALARM_VARIABLES)[number];

export const EMETER_ALARM_PHASES = ['a', 'b', 'c'] as const;
export type EmeterAlarmPhase = (typeof EMETER_ALARM_PHASES)[number];

export const EMETER_ALARM_RANGES: Record<
	EmeterAlarmVariable,
	{ min: number; max: number; step: number }
> = {
	voltage: { min: 0, max: 500, step: 0.1 },
	current: { min: 0, max: 400, step: 0.1 },
	power: { min: 0, max: 1000000, step: 1 },
};

export type EmeterAlarmSideState = {
	enabled: boolean;
	value: string;
};

export type EmeterAlarmVariableState = {
	enabled: boolean;
	under: EmeterAlarmSideState;
	over: EmeterAlarmSideState;
};

export type EmeterAlarmGroupState = Record<EmeterAlarmVariable, EmeterAlarmVariableState>;

export function getEmeterComponent(device: Device): 'em' | 'em1' {
	return get_gen_2_component_from_category(device) === 'em1' ? 'em1' : 'em';
}

export function isEmeterEm1(device: Device): boolean {
	return getEmeterComponent(device) === 'em1';
}

export function getEmeterSetConfigMethod(device: Device): string {
	return `${getEmeterComponent(device)}.SetConfig`;
}

export function getEmeterDataRpcNamespace(device: Device): 'EM1Data' | 'EMData' {
	return isGen2EM(device.type) || (isProThreeEM(device.type) && device.mode === 'monophase')
		? 'EM1Data'
		: 'EMData';
}

export function getDeviceFirmwareVersion(settings: Record<string, unknown> | undefined): string {
	const deviceInfo = settings?.DeviceInfo as { ver?: string; fw_id?: string } | undefined;
	const sys = settings?.sys as { device?: { fw_id?: string } } | undefined;
	return deviceInfo?.ver || sys?.device?.fw_id || deviceInfo?.fw_id || '';
}

export function deviceFirmwareAtLeast(
	settings: Record<string, unknown> | undefined,
	minVer: string,
): boolean {
	return compareFWVersions(getDeviceFirmwareVersion(settings), '>=', minVer);
}

export function emptyAlarmVariableState(): EmeterAlarmVariableState {
	return {
		enabled: false,
		under: { enabled: false, value: '' },
		over: { enabled: false, value: '' },
	};
}

export function emptyAlarmGroupState(): EmeterAlarmGroupState {
	return {
		voltage: emptyAlarmVariableState(),
		current: emptyAlarmVariableState(),
		power: emptyAlarmVariableState(),
	};
}

export function parseAlarmPair(pair: unknown): EmeterAlarmVariableState {
	if (!Array.isArray(pair) || pair.length < 2) {
		return emptyAlarmVariableState();
	}

	const under = pair[0];
	const over = pair[1];
	const underEnabled = under !== null && under !== undefined;
	const overEnabled = over !== null && over !== undefined;

	return {
		enabled: true,
		under: {
			enabled: underEnabled,
			value: underEnabled ? String(under) : '',
		},
		over: {
			enabled: overEnabled,
			value: overEnabled ? String(over) : '',
		},
	};
}

export function serializeAlarmPair(
	state: EmeterAlarmVariableState,
): [number | null, number | null] | null {
	if (!state.enabled) return null;

	const under =
		state.under.enabled && state.under.value !== '' && !Number.isNaN(Number(state.under.value))
			? Number(state.under.value)
			: null;
	const over =
		state.over.enabled && state.over.value !== '' && !Number.isNaN(Number(state.over.value))
			? Number(state.over.value)
			: null;

	return [under, over];
}

export function parseAlarmGroup(alarms: Record<string, unknown> | undefined): EmeterAlarmGroupState {
	const group = emptyAlarmGroupState();
	if (!alarms) return group;

	for (const variable of EMETER_ALARM_VARIABLES) {
		if (variable in alarms) {
			group[variable] = parseAlarmPair(alarms[variable]);
		}
	}

	return group;
}

export function serializeAlarmGroup(
	group: EmeterAlarmGroupState,
): Record<EmeterAlarmVariable, [number | null, number | null] | null> {
	return {
		voltage: serializeAlarmPair(group.voltage),
		current: serializeAlarmPair(group.current),
		power: serializeAlarmPair(group.power),
	};
}

export function cloneAlarmGroupState(group: EmeterAlarmGroupState): EmeterAlarmGroupState {
	return {
		voltage: {
			enabled: group.voltage.enabled,
			under: { ...group.voltage.under },
			over: { ...group.voltage.over },
		},
		current: {
			enabled: group.current.enabled,
			under: { ...group.current.under },
			over: { ...group.current.over },
		},
		power: {
			enabled: group.power.enabled,
			under: { ...group.power.under },
			over: { ...group.power.over },
		},
	};
}
